import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo, useState } from "react";
import { FormikErrors } from "formik";

import FormField from "../form-field/FormField";
import { Time } from "../timepicker/Time";
import TimePicker from "../timepicker/TimePicker";
import DatePicker from "../datepicker/DatePicker";
import { dateService } from "../../services/date-service/DateService";
import { IntegerNumberValidatorModel } from "../../services/form/validators/number/IntegerNumberValidator";
import { SubSection } from "./sections/DataSections";
import { CustomProperty as ICustomProperty } from "../../services/organization/ProjectPropertyStorage";
import { CustomProperty, CustomPropertyModel } from "./CustomProperty";
import { DropdownOption } from "../dropdown/Dropdown";
import { CustomProjectPropertyOption } from "./CustomProjectPropertyOption";
import { Dropdown } from "../dropdown/Dropdown";

interface EditCustomPropertiesProps {
  nestedFieldsPath?: string;
  properties: ICustomProperty[];
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  values: { [id: string]: any };
}
interface EditCustomPropertyProps {
  value: any;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  disabled?: boolean;
  property: CustomProperty;
  nestedFieldPath?: string;
}
export const EditCustomPropertiesInColumns = observer((props: EditCustomPropertiesProps) => {
  const { setFieldValue, nestedFieldsPath, properties, values } = props;

  const propertiesObjs: CustomProperty[] = properties.map((property) => {
    var customProperty = CustomPropertyModel.create(
      {
        label: property.label,
        value: property.value,
        columnId: property.columnId,
        index: property.index,
        type: property.type,
      },
      {}
    );

    customProperty.mapOptions(property.options!);
    return customProperty;
  });

  return (
    <SubSection>
      {propertiesObjs.map((property, key) => {
        if (property.isOfTypeText()) {
          return (
            <EditTextCustomProperty
              key={key}
              value={values[property.label]}
              property={property}
              setFieldValue={setFieldValue}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isOfTypeNumber()) {
          return (
            <EditNumberCustomProperty
              key={key}
              value={property.formatNumber(values[property.label])}
              property={property}
              setFieldValue={setFieldValue}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isOfTypeDropdown()) {
          const propertiesWithLabel = properties.find((p) => p.label === property.label);
          let selectedOption =
            (propertiesWithLabel?.options?.find(
              (o) => o.name === values[property.label]
            ) as DropdownOption) ?? null;
          return (
            <EditDropdownCustomProperty
              key={key}
              value={selectedOption}
              property={property}
              setFieldValue={setFieldValue}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isOfTypeDate()) {
          return (
            <EditDateCustomProperty
              key={key}
              value={values[property.label] || null}
              property={property}
              setFieldValue={setFieldValue}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isOfTypeTime()) {
          return (
            <EditTimeCustomProperty
              key={key}
              value={values[property.label]}
              property={property}
              setFieldValue={setFieldValue}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        return null;
      })}
    </SubSection>
  );
});

const EditTextCustomProperty = observer((props: EditCustomPropertyProps) => {
  const { value, nestedFieldPath, property } = props;

  const fieldName = useMemo(() => property.label, [property.label]);
  return (
    <FormField
      key={property.label}
      id={property.label}
      labelKey={property.label}
      fieldType="input"
      className="input"
      component="input"
      placeholderKey="general.add-value"
      fieldValue={value}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
    />
  );
});
const EditNumberCustomProperty = observer((props: EditCustomPropertyProps) => {
  const { value, nestedFieldPath, property } = props;
  const [requiredIntegerNumberValidator] = useState(() =>
    IntegerNumberValidatorModel.create({
      isRequired: false,
    })
  );

  const fieldName = useMemo(() => property.label, [property.label]);
  return (
    <FormField
      key={property.label}
      id={property.label}
      labelKey={property.label}
      fieldType="input"
      className="input"
      component="input"
      placeholderKey="general.add-value"
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredIntegerNumberValidator}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
    />
  );
});

const EditDropdownCustomProperty = observer((props: EditCustomPropertyProps) => {
  const { nestedFieldPath, property, setFieldValue } = props;
  const value = props.value as DropdownOption;

  const optionsList = useMemo(() => {
    return property
      .getSortedOptions(property.options! as CustomProjectPropertyOption[])
      .map((option) => {
        return {
          id: option.id,
          name: option.name,
        };
      });
  }, [property]);

  return (
    <FormField
      key={property.label}
      id={property.label}
      className="select"
      labelKey={property.label}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${property.label}` : property.label}
      fieldValue={value}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${property.label}` : property.label}
      children={() => (
        <Dropdown
          selectedOption={value}
          options={optionsList}
          fieldName={nestedFieldPath ? `${nestedFieldPath}.${property.label}` : property.label}
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value?.name ?? "");
          }}
          canClearSelection
        />
      )}
    />
  );
});

const EditDateCustomProperty = observer((props: EditCustomPropertyProps) => {
  const { value, setFieldValue, nestedFieldPath, property } = props;
  const fieldName = useMemo(() => property.label, [property.label]);

  const datePickerValue = useMemo(() => {
    if (value) return new Date(value);

    return value;
  }, [value]);

  const updateDate = useCallback(
    (date: any) => {
      if (setFieldValue)
        setFieldValue(
          nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
          date && dateService.dateToUTC(date)
        );
    },
    [fieldName, nestedFieldPath, setFieldValue]
  );

  return (
    <FormField
      key={property.label}
      id={property.label}
      labelKey={property.label}
      fieldName={fieldName}
      fieldValue={value}
      component={() => (
        <DatePicker
          value={datePickerValue}
          onChange={(date) => updateDate(date)}
          placeholderKey="general.add-value"
        />
      )}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
    />
  );
});

const EditTimeCustomProperty = observer((props: EditCustomPropertyProps) => {
  const { value, setFieldValue, nestedFieldPath, property } = props;
  const fieldName = useMemo(() => property.label, [property.label]);

  const time = useMemo((): Time => {
    const time = (value as string).split(":");
    if (time[0] !== "") {
      return new Time(parseInt(time[0]), parseInt(time[1]), parseInt(time[2]));
    }
    return new Time(0, 0, 0);
  }, [value]);

  const updateTime = useCallback(
    (time: Time) => {
      if (setFieldValue) {
        setFieldValue(
          nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
          time.format()
        );
      }
    },
    [fieldName, nestedFieldPath, setFieldValue]
  );

  return (
    <FormField
      key={property.label}
      id={property.label}
      fieldType="input"
      labelKey={property.label}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={time}
      component={() => <TimePicker value={time} onChange={updateTime} />}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
    />
  );
});
